import { PageType } from '@app-types/PageType'
import { useUserData } from '@contexts/UserDataContext'
import classNames from 'classnames'
import { DuaPartType } from './DuaPageComponent'
import DuaPartBookmark from './DuaPartBookmark'
import DuaRepeatCounter from './DuaRepeatCounter'

type DuaPartProps = {
  isLastItem: boolean
  part: DuaPartType
  type: PageType
  page: number
  partKey: number
  noBookmark?: boolean
}

/**
 * Renders an individual dua section as a modern rounded card with a number badge,
 * bookmark toggle, centered Arabic recitation, transliteration, and Turkish translation.
 */
const DuaPart = ({
  isLastItem,
  part,
  type,
  page,
  partKey,
  noBookmark,
}: DuaPartProps) => {
  const { userData } = useUserData()

  // Check if only one recitation form (Arabic, Turkish meal, or Latin transliteration) is present
  const onlyOneTypeExist =
    (part.arab && !part.meal && !part.latin) ||
    (!part.arab && part.meal && !part.latin) ||
    (!part.arab && !part.meal && part.latin)

  const showLatin = userData.duaLatinVisibility === 'show' || onlyOneTypeExist
  const showMeal = userData.duaMealVisibility === 'show' || onlyOneTypeExist

  const finalShowLatin =
    (showLatin && Boolean(part.latin)) ||
    (showMeal && !part.meal && Boolean(part.latin))
  const finalShowMeal =
    (showMeal && Boolean(part.meal)) ||
    (showLatin && !part.latin && Boolean(part.meal))

  // Determine section display number for the top badge
  const sectionNumber = part.no ?? partKey + 1

  return (
    <article
      className={classNames(
        'relative rounded-2xl px-3 py-4 sm:p-6 transition-all duration-300 ayah-id',
        'bg-white dark:bg-[#13222e] border border-slate-200 dark:border-[#1e3445]/60 shadow-sm',
        {
          'border-red-500/50 dark:border-red-900/50':
            isLastItem && type === 'cevsen',
        },
      )}
      id={`bolum-${partKey}`}
      data-word-ayah-id={partKey}
      data-ayah-id={partKey}
    >
      {/* Top Header Row of the Card: Number Badge and Action Controls */}
      <div className="flex items-center justify-between mb-2 sm:mb-3">
        <div className="flex items-center gap-2">
          <span
            aria-label={`Bölüm ${sectionNumber}`}
            className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 text-xs sm:text-sm font-bold rounded-lg bg-teal-50 dark:bg-[#0c3947] text-teal-600 dark:text-teal-400 border border-teal-200 dark:border-teal-500/20"
          >
            {sectionNumber}
          </span>
          {part.note && (
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
              {part.note}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          {part.repeat && <DuaRepeatCounter key={part.no} repeat={part.repeat} />}
          {!noBookmark && (
            <DuaPartBookmark type={type} page={page} partKey={partKey} />
          )}
        </div>
      </div>

      {/* Card Content Body */}
      <div className="flex flex-col">
        {/* Arabic Text (Centered and Prominent) */}
        {(userData.duaArabicVisibility === 'show' || onlyOneTypeExist) && (
          <div
            className={classNames(
              'dua-arabic whitespace-pre-wrap leading-[2.2] tracking-normal text-slate-900 dark:text-white my-3',
              userData.arabicFontFamily,
              {
                'text-center': userData.arabicFontAlignment === 'middle',
                'text-right': userData.arabicFontAlignment !== 'middle',
              },
            )}
          >
            {part.arab}
          </div>
        )}

        {/* Latin Transliteration / Okunuş (Left Aligned, Muted) */}
        {finalShowLatin && part.latin && (
          <div className="dua-meal whitespace-pre-wrap text-sm leading-relaxed tracking-normal text-slate-600 dark:text-slate-400 text-left mt-2">
            {part.latin}
          </div>
        )}

        {/* Horizontal Divider between Transliteration and Translation */}
        {finalShowLatin && part.latin && finalShowMeal && part.meal && (
          <div
            aria-hidden="true"
            className="border-t border-slate-200/80 dark:border-slate-700/50 my-3.5"
          />
        )}

        {/* Turkish Translation / Meal (Left Aligned, Legible) */}
        {finalShowMeal && part.meal && (
          <div className="dua-meal whitespace-pre-wrap text-sm sm:text-base leading-relaxed tracking-normal text-slate-800 dark:text-slate-200 text-left">
            {part.meal}
          </div>
        )}

        {/* Footnote / After Note (if any) */}
        {part.afterNote && (
          <p className="mt-3 text-center text-xs font-medium italic text-slate-400 dark:text-slate-500">
            {part.afterNote}
          </p>
        )}
      </div>
    </article>
  )
}

export default DuaPart
